import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
  Stack,
  Box,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Work,
  Code,
  Psychology,
  Schedule,
  Group,
  CheckCircle,
  Build,
  Link,
  PlayArrow,
  Image,
  Info,
} from '@mui/icons-material';

function FeatureFull({ feature, open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!feature) return null;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Enterprise':
        return <Work sx={{ fontSize: { xs: 20, sm: 24 } }} />;
      case 'Independent':
        return <Code sx={{ fontSize: { xs: 20, sm: 24 } }} />;
      case 'Prototype':
        return <Psychology sx={{ fontSize: { xs: 20, sm: 24 } }} />;
      default:
        return null;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Enterprise':
        return '#00ffff';
      case 'Independent':
        return '#ff6b35';
      case 'Prototype':
        return '#ffab00';
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: '90vh',
          background: 'linear-gradient(145deg, #0a0a0a, #111111)',
          border: `1px solid ${alpha(getCategoryColor(feature.category), 0.3)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, sm: 3 },
          background: `
            linear-gradient(145deg, #0a0a0a, #111111),
            radial-gradient(circle at top right, ${alpha(getCategoryColor(feature.category), 0.1)} 0%, transparent 50%)
          `,
          borderBottom: `1px solid ${alpha(getCategoryColor(feature.category), 0.3)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: '#00ff41',
                fontFamily: '"Fira Code", monospace',
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
                lineHeight: { xs: 1.3, sm: 1.2 },
              }}
            >
              {feature.name}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha('#ff6b35', 0.1),
                color: '#ff6b35',
              },
              minWidth: { xs: 44, sm: 48 },
              minHeight: { xs: 44, sm: 48 },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: getCategoryColor(feature.category),
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              lineHeight: 1.4,
              fontFamily: '"Inter", sans-serif',
            }}
          >
            {feature.summary}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: { xs: 2, sm: 3 },
              lineHeight: 1.7,
              color: 'text.primary',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontFamily: '"Inter", sans-serif',
            }}
          >
            {feature.description}
          </Typography>

          {(feature.video_id || feature.image) && (
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#00ff41',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                }}
              >
                {feature.video_id ? (
                  <PlayArrow sx={{ color: '#00ff41', fontSize: { xs: 18, sm: 24 } }} />
                ) : (
                  <Image sx={{ color: '#00ff41', fontSize: { xs: 18, sm: 24 } }} />
                )}
                {feature.video_id ? 'Project Overview' : 'Project Preview'}
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: feature.video_id ? '56.25%' : 'auto',
                  height: feature.video_id ? 0 : 'auto',
                  overflow: 'hidden',
                  borderRadius: 2,
                  border: `1px solid ${alpha(getCategoryColor(feature.category), 0.3)}`,
                  boxShadow: `0 8px 25px ${alpha(getCategoryColor(feature.category), 0.2)}`,
                }}
              >
                {feature.video_id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${feature.video_id}`}
                    title={`${feature.name} Demo`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '8px',
                    }}
                  />
                ) : (
                  <img
                    src={feature.image}
                    alt={`${feature.name} Preview`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      display: 'block',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={{ xs: 2, sm: 4 }}>
            <Grid item xs={12} md={8}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#00ff41',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                }}
              >
                <CheckCircle sx={{ color: '#00ff41', fontSize: { xs: 18, sm: 24 } }} />
                Key Highlights
              </Typography>
              <List dense sx={{ pl: { xs: 0, sm: 1 } }}>
                {feature.highlights.map((highlight, index) => (
                  <ListItem key={index} sx={{ px: 0, py: { xs: 0.5, sm: 1 } }}>
                    <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 } }}>
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 16, sm: 20 },
                          color: '#00ff41',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={highlight}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          lineHeight: 1.5,
                          fontFamily: '"Inter", sans-serif',
                          color: 'text.primary',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      color: '#00ffff',
                      fontFamily: '"Fira Code", monospace',
                    }}
                  >
                    Project Details
                  </Typography>
                  <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ fontSize: { xs: 18, sm: 20 }, color: getCategoryColor(feature.category) }} />
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontFamily: '"Inter", sans-serif',
                          }}
                        >
                          Start Date
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight={500}
                          sx={{ 
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            fontFamily: '"Fira Code", monospace',
                            color: 'text.primary',
                          }}
                        >
                          {feature.start_date}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ fontSize: { xs: 18, sm: 20 }, color: getCategoryColor(feature.category) }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Duration
                          </Typography>
                          <Tooltip 
                            title="Time period when the project was released or completed" 
                            arrow
                            placement="top"
                          >
                            <Info 
                              sx={{ 
                                fontSize: { xs: 12, sm: 14 }, 
                                color: 'text.secondary',
                                cursor: 'help',
                                '&:hover': {
                                  color: getCategoryColor(feature.category),
                                },
                                transition: 'color 0.2s ease',
                              }} 
                            />
                          </Tooltip>
                        </Box>
                        <Typography 
                          variant="body1" 
                          fontWeight={500}
                          sx={{ 
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            fontFamily: '"Fira Code", monospace',
                            color: 'text.primary',
                          }}
                        >
                          {feature.duration}
                        </Typography>
                      </Box>
                    </Box>
                    {feature.link && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link sx={{ fontSize: { xs: 18, sm: 20 }, color: getCategoryColor(feature.category) }} />
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Project Link
                          </Typography>
                          <Typography 
                            component="a"
                            href={feature.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body1" 
                            fontWeight={500}
                            sx={{ 
                              fontSize: { xs: '0.85rem', sm: '1rem' },
                              fontFamily: '"Fira Code", monospace',
                              color: getCategoryColor(feature.category),
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#00ff41',
                              },
                              transition: 'color 0.3s ease',
                            }}
                          >
                            Visit Project
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: alpha(getCategoryColor(feature.category), 0.2) }} />

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#00ffff',
                      fontFamily: '"Fira Code", monospace',
                    }}
                  >
                    <Build sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Technologies
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {feature.technologies.map((tech) => (
                      <Chip
                        key={tech}
                        label={tech}
                        size={isMobile ? 'small' : 'medium'}
                        variant="outlined"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          fontFamily: '"Fira Code", monospace',
                          background: `linear-gradient(145deg, ${alpha(getCategoryColor(feature.category), 0.1)}, transparent)`,
                          borderColor: alpha(getCategoryColor(feature.category), 0.3),
                          color: getCategoryColor(feature.category),
                          '&:hover': {
                            bgcolor: alpha(getCategoryColor(feature.category), 0.1),
                            borderColor: getCategoryColor(feature.category),
                            transform: isMobile ? 'none' : 'scale(1.05)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default FeatureFull; 